import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';
import Spinner from './components/ui/Spinner';

const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ProjectsList = lazy(() => import('./pages/ProjectsList'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const JobSubmit = lazy(() => import('./pages/JobSubmit'));
const JobMonitor = lazy(() => import('./pages/JobMonitor'));
const AssetDetail = lazy(() => import('./pages/AssetDetail'));
const Billing = lazy(() => import('./pages/Billing'));
const Users = lazy(() => import('./pages/Users'));
const Settings = lazy(() => import('./pages/Settings'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminTenants = lazy(() => import('./pages/admin/AdminTenants'));
const AdminJobs = lazy(() => import('./pages/admin/AdminJobs'));
const AdminBilling = lazy(() => import('./pages/admin/AdminBilling'));
const AdminAudit = lazy(() => import('./pages/admin/AdminAudit'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AllJobs = lazy(() => import('./pages/AllJobs'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const NotFound = lazy(() => import('./pages/NotFound'));

function Fallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function wrap(el: React.ReactNode) {
  return <Suspense fallback={<Fallback />}>{el}</Suspense>;
}

export const router = createBrowserRouter([
  {
    element: <AuthProvider />,
    children: [
      { path: '/login', element: wrap(<Login />) },
      { path: '/forgot-password', element: wrap(<ForgotPassword />) },
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <AppLayout variant="customer" />,
            children: [
              { index: true, element: <Navigate to="/dashboard" replace /> },
              { path: 'dashboard', element: wrap(<Dashboard />) },
              { path: 'projects', element: wrap(<ProjectsList />) },
              { path: 'jobs', element: wrap(<AllJobs />) },
              { path: 'projects/:projectId', element: wrap(<ProjectDetail />) },
              { path: 'projects/:projectId/jobs/new', element: wrap(<JobSubmit />) },
              { path: 'projects/:projectId/jobs/:jobId', element: wrap(<JobMonitor />) },
              { path: 'projects/:projectId/assets/:assetId', element: wrap(<AssetDetail />) },
              { path: 'billing', element: wrap(<Billing />) },
              { path: 'users', element: wrap(<Users />) },
              { path: 'settings', element: wrap(<Settings />) },
            ],
          },
        ],
      },
      {
        path: 'admin',
        element: <ProtectedRoute requiredRole="super-admin" />,
        children: [
          {
            element: <AppLayout variant="admin" />,
            children: [
              { index: true, element: <Navigate to="/admin/dashboard" replace /> },
              { path: 'dashboard', element: wrap(<AdminDashboard />) },
              { path: 'tenants', element: wrap(<AdminTenants />) },
              { path: 'jobs', element: wrap(<AdminJobs />) },
              { path: 'billing', element: wrap(<AdminBilling />) },
              { path: 'audit', element: wrap(<AdminAudit />) },
              { path: 'users', element: wrap(<AdminUsers />) },
              { path: 'settings', element: wrap(<Settings />) },
            ],
          },
        ],
      },
      { path: '*', element: wrap(<NotFound />) },
    ],
  },
]);
